import React, { createContext, useContext, useMemo } from 'react';
import {
  DonorNameMatchRange,
  findDonorNameMatches,
} from 'shared/utils/donorNameMatch';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import { getDonorNameFieldLabels } from './donorNameFlagUtils';

export interface DonorNameAnswer {
  questionId: number;
  text: string | null | undefined;
}

interface DonorNameFlagContextProps {
  donorName: string | null | undefined;
  enabled: boolean;
  fieldLabels: string[];
}

const DonorNameFlagContext = createContext<DonorNameFlagContextProps>({
  donorName: undefined,
  enabled: false,
  fieldLabels: [],
});

export const DonorNameFlagProvider: React.FC<{
  donorName: string | null | undefined;
  answers: DonorNameAnswer[];
  children: React.ReactNode;
}> = ({ donorName, answers, children }) => {
  const enabled = useFeatureFlags('levelOneFeedback');
  const value = useMemo(
    () => ({
      donorName,
      enabled: Boolean(enabled),
      fieldLabels:
        enabled && donorName ? getDonorNameFieldLabels(answers, donorName) : [],
    }),
    [donorName, enabled, answers],
  );
  return (
    <DonorNameFlagContext.Provider value={value}>
      {children}
    </DonorNameFlagContext.Provider>
  );
};

export const useDonorNameFlag = () => {
  const { donorName, enabled, fieldLabels } = useContext(DonorNameFlagContext);
  return useMemo(() => {
    const detect = (text: string | null | undefined): DonorNameMatchRange[] =>
      enabled && donorName ? findDonorNameMatches(text, donorName) : [];
    return {
      donorName,
      enabled,
      fieldLabels,
      detect,
      has: (text: string | null | undefined) => detect(text).length > 0,
    };
  }, [donorName, enabled, fieldLabels]);
};
